import React, { useState, useEffect } from 'react';
import { Info, Plus } from 'lucide-react';
import Button from 'ui/Button';
import Input from 'ui/Input';
import Select from 'ui/Select';
import Modal from 'ui/Modal';
import Loading from 'ui/Loading';
import Section from 'ui/Section';
import { Badge } from 'ui/shadcn';
import type { BadgeProps } from 'ui/shadcn/badge';
import { StaffApis } from 'apis/staff';
import { toast } from 'react-toastify';
import moment from 'moment';

interface Education {
    id?: number;
    education_type: string;
    course_level: string;
    start_date?: string;
    end_date?: string;
    expiry_date?: string;
    remarks?: string;
    assignment_type?: string;
    management_number?: string;
    previous_training_date?: string;
    scheduled_education?: string;
    display_order?: number;
}

interface EducationManagementProps {
    staffId: number;
}

type BadgeVariant = BadgeProps['variant'];

interface ExpiryStatus {
    label: string;
    variant: BadgeVariant;
    className?: string;
    tooltip: string;
    daysText?: string;
}

// 만료됨은 만료임박(danger 톤)보다 한 단계 강하게 — 시맨틱 토큰 solid
const EXPIRED_BADGE_CLASS = 'bg-destructive text-destructive-foreground';

// 만료일 계산 헬퍼 함수
const calculatePQExpiry = (endDate: string): string => {
    if (!endDate) return '';
    return moment(endDate).add(3, 'years').format('YYYY-MM-DD');
};

const calculateFireSafetyExpiry = (endDate: string): string => {
    if (!endDate) return '';
    const endYear = moment(endDate).year();
    const expiryYear = endYear + 2;
    return moment(`${expiryYear}-12-31`).format('YYYY-MM-DD');
};

// 만료 상태 계산 헬퍼 함수
const getExpiryStatus = (education: Education): ExpiryStatus => {
    const expiryDate = education.expiry_date;
    const previousTrainingDate = education.previous_training_date;
    const educationType = education.education_type;

    if (!expiryDate) {
        return {
            label: '미설정',
            variant: 'secondary',
            tooltip: '만료일이 설정되지 않았습니다'
        };
    }

    const today = moment();
    let effectiveExpiryDate = moment(expiryDate);
    let hasPreviousTraining = false;

    // 당해 교육현황이 있으면 실제 만료일 재계산
    if (previousTrainingDate) {
        hasPreviousTraining = true;
        if (educationType === 'ELECTRIC_PQ' || educationType === 'FIRE_PQ') {
            // PQ 교육: 당해 교육현황 + 3년
            effectiveExpiryDate = moment(previousTrainingDate).add(3, 'years');
        } else if (educationType === 'FIRE_SAFETY') {
            // 소방안전원: 당해 교육현황 년도 + 2년 연말
            const trainingYear = moment(previousTrainingDate).year();
            const expiryYear = trainingYear + 2;
            effectiveExpiryDate = moment(`${expiryYear}-12-31`);
        }
    }

    const daysUntilExpiry = effectiveExpiryDate.diff(today, 'days');
    const displayDate = effectiveExpiryDate.format('YYYY-MM-DD');

    // 당해 교육현황이 있고 만료일이 90일 이상 남았으면 "당해이수"
    if (hasPreviousTraining && daysUntilExpiry >= 90) {
        return {
            label: '당해이수',
            variant: 'success',
            tooltip: `당해년도 교육 이수 완료 (만료일: ${displayDate}, ${daysUntilExpiry}일 남음)`
        };
    }

    if (daysUntilExpiry < 0) {
        return {
            label: '만료됨',
            variant: 'danger',
            className: EXPIRED_BADGE_CLASS,
            tooltip: `${Math.abs(daysUntilExpiry)}일 경과 (만료일: ${displayDate})`,
            daysText: `D+${Math.abs(daysUntilExpiry)}`
        };
    } else if (daysUntilExpiry <= 30) {
        return {
            label: '만료임박',
            variant: 'danger',
            tooltip: `${daysUntilExpiry}일 남음 (만료일: ${displayDate}) - 30일 이내`,
            daysText: `D-${daysUntilExpiry}`
        };
    } else if (daysUntilExpiry <= 90) {
        return {
            label: '주의',
            variant: 'warning',
            tooltip: `${daysUntilExpiry}일 남음 (만료일: ${displayDate}) - 90일 이내`,
            daysText: `D-${daysUntilExpiry}`
        };
    } else {
        return {
            label: '정상',
            variant: 'default',
            tooltip: `${daysUntilExpiry}일 남음 (만료일: ${displayDate}) - 90일 이상`,
            daysText: `D-${daysUntilExpiry}`
        };
    }
};

const STATUS_LEGEND: Array<{
    label: string;
    variant: BadgeVariant;
    className?: string;
    title: string;
    description: string;
}> = [
    { label: '당해이수', variant: 'success', title: '당해년도 교육 이수 완료', description: '올해 교육을 완료하여 최신 상태입니다.' },
    { label: '정상', variant: 'default', title: '만료일까지 90일 이상 남음', description: '정상적으로 사용 가능한 상태입니다.' },
    { label: '주의', variant: 'warning', title: '만료일까지 31~90일 남음', description: '갱신 준비가 필요합니다.' },
    { label: '만료임박', variant: 'danger', title: '만료일까지 30일 이내', description: '빠른 시일 내에 갱신이 필요합니다.' },
    { label: '만료됨', variant: 'danger', className: EXPIRED_BADGE_CLASS, title: '만료일이 지남', description: '즉시 갱신이 필요합니다.' },
    { label: '미설정', variant: 'secondary', title: '만료일이 설정되지 않음', description: '만료일을 입력해주세요.' }
];

const formatDate = (value?: string) => (value ? moment(value).format('YYYY-MM-DD') : '-');

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <div className="min-w-0">
        <dt className="text-xs text-muted-foreground">{label}</dt>
        <dd className="mt-0.5 text-md text-foreground tabular-nums break-words">{children}</dd>
    </div>
);

const EducationManagement: React.FC<EducationManagementProps> = ({ staffId }) => {
    const [educations, setEducations] = useState<Education[]>([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [showStatusInfo, setShowStatusInfo] = useState(false);
    const [editingEducation, setEditingEducation] = useState<Education | null>(null);

    const [formData, setFormData] = useState<Education>({
        education_type: 'ELECTRIC_PQ',
        course_level: 'BASIC',
        start_date: '',
        end_date: '',
        expiry_date: '',
        remarks: '',
        assignment_type: '',
        management_number: '',
        previous_training_date: '',
        scheduled_education: '',
        display_order: 0
    });

    useEffect(() => {
        fetchEducations();
    }, [staffId]);

    const fetchEducations = async () => {
        try {
            setLoading(true);
            const response = await StaffApis.getEducations(staffId);
            setEducations(response || []);
        } catch (error) {
            console.error('Failed to fetch educations:', error);
            toast.error('교육 정보를 불러오는데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (education?: Education, educationType?: string) => {
        if (education && education.id) {
            // 기존 교육 수정
            setEditingEducation(education);
            setFormData({
                ...education,
                start_date: education.start_date ? moment(education.start_date).format('YYYY-MM-DD') : '',
                end_date: education.end_date ? moment(education.end_date).format('YYYY-MM-DD') : '',
                expiry_date: education.expiry_date ? moment(education.expiry_date).format('YYYY-MM-DD') : '',
                previous_training_date: education.previous_training_date ? moment(education.previous_training_date).format('YYYY-MM-DD') : ''
            });
        } else {
            // 새 교육 추가
            setEditingEducation(null);
            const newEducationType = educationType || 'ELECTRIC_PQ';
            setFormData({
                education_type: newEducationType,
                course_level: newEducationType === 'FIRE_SAFETY' ? 'SINGLE' : 'BASIC',
                start_date: '',
                end_date: '',
                expiry_date: '',
                remarks: '',
                assignment_type: '',
                management_number: '',
                previous_training_date: '',
                scheduled_education: '',
                display_order: 0
            });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingEducation(null);
    };

    // 종료일 변경 시 만료일 자동 계산
    const handleEndDateChange = (endDate: string) => {
        setFormData(prev => {
            const newFormData = { ...prev, end_date: endDate };

            // PQ 교육인 경우 만료일 자동 계산 (종료일 + 3년)
            if (prev.education_type === 'ELECTRIC_PQ' || prev.education_type === 'FIRE_PQ') {
                newFormData.expiry_date = calculatePQExpiry(endDate);
            }
            // 소방안전원 교육인 경우 만료일 자동 계산 (교육년도 + 2년, 연말)
            else if (prev.education_type === 'FIRE_SAFETY') {
                newFormData.expiry_date = calculateFireSafetyExpiry(endDate);
            }

            return newFormData;
        });
    };

    const handleSaveEducation = async () => {
        try {
            // 빈 문자열을 null로 변환하고, 교육 타입에 맞지 않는 필드 제거
            const cleanedData: any = {
                education_type: formData.education_type,
                course_level: formData.course_level,
                start_date: formData.start_date || null,
                end_date: formData.end_date || null,
                expiry_date: formData.expiry_date || null,
                remarks: formData.remarks || null,
                display_order: formData.display_order || 0
            };

            // PQ 교육 전용 필드
            if (formData.education_type === 'ELECTRIC_PQ' || formData.education_type === 'FIRE_PQ') {
                cleanedData.scheduled_education = formData.scheduled_education || null;
                cleanedData.previous_training_date = formData.previous_training_date || null;
            }

            // 소방안전원 교육 전용 필드
            if (formData.education_type === 'FIRE_SAFETY') {
                cleanedData.assignment_type = formData.assignment_type || null;
                cleanedData.management_number = formData.management_number || null;
                cleanedData.previous_training_date = formData.previous_training_date || null;
                cleanedData.scheduled_education = formData.scheduled_education || null;
            }

            if (editingEducation?.id) {
                await StaffApis.updateEducation(editingEducation.id, cleanedData);
                toast.success('교육 정보가 수정되었습니다.');
            } else {
                await StaffApis.addEducation(staffId, cleanedData);
                toast.success('교육 정보가 추가되었습니다.');
            }

            await fetchEducations();
            handleCloseModal();
        } catch (error) {
            console.error('Failed to save education:', error);
            toast.error('교육 정보 저장에 실패했습니다.');
        }
    };

    const handleDeleteEducation = async (educationId: number) => {
        if (!window.confirm('이 교육 정보를 삭제하시겠습니까?')) {
            return;
        }

        try {
            await StaffApis.removeEducation(educationId);
            await fetchEducations();
            toast.success('교육 정보가 삭제되었습니다.');
        } catch (error) {
            console.error('Failed to delete education:', error);
            toast.error('교육 정보 삭제에 실패했습니다.');
        }
    };

    // 교육 타입별로 그룹화 및 정렬 (기본과정 우선)
    const groupedEducations = {
        ELECTRIC_PQ: educations
            .filter(e => e.education_type === 'ELECTRIC_PQ')
            .sort((a, b) => {
                // 기본과정(BASIC)을 먼저, 심화과정(ADVANCED)을 나중에
                if (a.course_level === 'BASIC' && b.course_level === 'ADVANCED') return -1;
                if (a.course_level === 'ADVANCED' && b.course_level === 'BASIC') return 1;
                return 0;
            }),
        FIRE_PQ: educations
            .filter(e => e.education_type === 'FIRE_PQ')
            .sort((a, b) => {
                // 기본과정(BASIC)을 먼저, 심화과정(ADVANCED)을 나중에
                if (a.course_level === 'BASIC' && b.course_level === 'ADVANCED') return -1;
                if (a.course_level === 'ADVANCED' && b.course_level === 'BASIC') return 1;
                return 0;
            }),
        FIRE_SAFETY: educations.filter(e => e.education_type === 'FIRE_SAFETY')
    };

    const renderEducationItem = (education: Education) => {
        const status = getExpiryStatus(education);
        const isFireSafety = education.education_type === 'FIRE_SAFETY';

        return (
            <div key={education.id} className="px-4 py-3">
                <div className="flex items-center justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-2">
                        <h4 className="text-md font-semibold text-foreground">
                            {education.course_level === 'BASIC' && '기본과정'}
                            {education.course_level === 'ADVANCED' && '심화과정'}
                            {education.course_level === 'SINGLE' && '소방안전원 교육'}
                        </h4>
                        <Badge
                            variant={status.variant}
                            className={`tabular-nums ${status.className || ''}`}
                            title={status.tooltip}
                        >
                            {status.label}{status.daysText && ` ${status.daysText}`}
                        </Badge>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                        <Button size="small" variant="outline" onClick={() => handleOpenModal(education)}>
                            수정
                        </Button>
                        <Button
                            size="small"
                            variant="danger-outline"
                            onClick={() => education.id && handleDeleteEducation(education.id)}
                        >
                            삭제
                        </Button>
                    </div>
                </div>

                <dl className={`mt-3 grid grid-cols-2 gap-x-6 gap-y-3 ${isFireSafety ? 'lg:grid-cols-3' : 'lg:grid-cols-4'}`}>
                    {isFireSafety ? (
                        // 소방안전원 전용 표시
                        <>
                            <Field label="선임구분">{education.assignment_type || '-'}</Field>
                            <Field label="관리번호">{education.management_number || '-'}</Field>
                            <Field label="수료일">{formatDate(education.end_date)}</Field>
                            <Field label="만료일">
                                <span className="font-medium">{formatDate(education.expiry_date)}</span>
                            </Field>
                            <Field label="당해 교육현황">{formatDate(education.previous_training_date)}</Field>
                            <Field label="교육예정">{education.scheduled_education || '-'}</Field>
                        </>
                    ) : (
                        // PQ 교육: 교육기간, 만료일, 당해 교육현황 표시
                        <>
                            <Field label="교육기간">
                                {education.start_date && education.end_date
                                    ? `${moment(education.start_date).format('YYYY-MM-DD')} ~ ${moment(education.end_date).format('YYYY-MM-DD')}`
                                    : education.start_date
                                        ? moment(education.start_date).format('YYYY-MM-DD')
                                        : education.end_date
                                            ? moment(education.end_date).format('YYYY-MM-DD')
                                            : '-'
                                }
                            </Field>
                            <Field label="만료일">
                                <span className="font-medium">{formatDate(education.expiry_date)}</span>
                            </Field>
                            <Field label="당해 교육현황">{formatDate(education.previous_training_date)}</Field>
                            <Field label="교육예정">{education.scheduled_education || '-'}</Field>
                        </>
                    )}
                </dl>

                {education.remarks && (
                    <dl className="mt-3 border-t border-border pt-3">
                        <Field label="비고">{education.remarks}</Field>
                    </dl>
                )}
            </div>
        );
    };

    const renderGroup = (
        title: string,
        sectionKey: string,
        educationList: Education[],
        addButtonLabel: string
    ) => {
        return (
            <div key={sectionKey} className="border-b border-border last:border-b-0">
                <div className="flex items-center justify-between gap-3 border-b border-border bg-neutral-50 px-4 py-2">
                    <div className="flex items-center gap-2">
                        <h3 className="text-sm font-semibold text-neutral-700">{title}</h3>
                        <Badge variant="secondary" className="tabular-nums">
                            {educationList.length}
                        </Badge>
                    </div>
                    <Button
                        variant="outline"
                        size="small"
                        icon={<Plus />}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleOpenModal(undefined, sectionKey);
                        }}
                    >
                        {addButtonLabel}
                    </Button>
                </div>

                {educationList.length > 0 ? (
                    <div className="divide-y divide-border">
                        {educationList.map(renderEducationItem)}
                    </div>
                ) : (
                    <p className="px-4 py-8 text-center text-sm text-muted-foreground">
                        등록된 교육 정보가 없습니다.
                    </p>
                )}
            </div>
        );
    };

    return (
        <>
            <Section
                title="교육 관리"
                actions={
                    <Button
                        variant="outline"
                        size="small"
                        icon={<Info />}
                        onClick={() => setShowStatusInfo(true)}
                    >
                        상태 기준 보기
                    </Button>
                }
                flush
            >
                {loading ? (
                    <div className="py-8">
                        <Loading text="교육 정보를 불러오는 중..." />
                    </div>
                ) : (
                    <>
                        {renderGroup('전기PQ 교육', 'ELECTRIC_PQ', groupedEducations.ELECTRIC_PQ, '전기PQ 추가')}
                        {renderGroup('소방PQ 교육', 'FIRE_PQ', groupedEducations.FIRE_PQ, '소방PQ 추가')}
                        {renderGroup('소방안전원 교육', 'FIRE_SAFETY', groupedEducations.FIRE_SAFETY, '소방안전원 추가')}
                    </>
                )}
            </Section>

            {/* 교육 추가/수정 모달 */}
            <Modal
                isOpen={showModal}
                onClose={handleCloseModal}
                title={editingEducation ? '교육 정보 수정' : '교육 정보 추가'}
                footer={
                    <>
                        <Button variant="outline" onClick={handleCloseModal}>
                            취소
                        </Button>
                        <Button onClick={handleSaveEducation}>
                            {editingEducation ? '수정' : '추가'}
                        </Button>
                    </>
                }
            >
                <div className="space-y-4">
                    <Select
                        label="교육 타입"
                        value={formData.education_type}
                        onChange={(e) => setFormData({
                            ...formData,
                            education_type: e.target.value,
                            course_level: e.target.value === 'FIRE_SAFETY' ? 'SINGLE' : 'BASIC'
                        })}
                        options={[
                            { value: 'ELECTRIC_PQ', label: '전기PQ' },
                            { value: 'FIRE_PQ', label: '소방PQ' },
                            { value: 'FIRE_SAFETY', label: '소방안전원 교육' }
                        ]}
                    />

                    {formData.education_type !== 'FIRE_SAFETY' && (
                        <Select
                            label="과정 레벨"
                            value={formData.course_level}
                            onChange={(e) => setFormData({ ...formData, course_level: e.target.value })}
                            options={[
                                { value: 'BASIC', label: '기본과정' },
                                { value: 'ADVANCED', label: '심화과정' }
                            ]}
                        />
                    )}

                    {/* 소방안전원 전용 필드 */}
                    {formData.education_type === 'FIRE_SAFETY' && (
                        <>
                            <Input
                                label="선임구분"
                                type="text"
                                value={formData.assignment_type || ''}
                                onChange={(e) => setFormData({ ...formData, assignment_type: e.target.value })}
                                placeholder="예: 감리, 전기공사업"
                            />

                            <Input
                                label="관리번호"
                                type="text"
                                value={formData.management_number || ''}
                                onChange={(e) => setFormData({ ...formData, management_number: e.target.value })}
                                placeholder="예: 012995-0"
                            />

                            <Input
                                label="수료일"
                                type="date"
                                value={formData.end_date}
                                onChange={(e) => handleEndDateChange(e.target.value)}
                            />

                            <div>
                                <Input
                                    label="만료일"
                                    type="date"
                                    value={formData.expiry_date}
                                    onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                                />
                                <p className="mt-1 text-xs text-muted-foreground">수료일 입력 시 자동으로 계산됩니다 (수료년도 + 2년 연말)</p>
                            </div>

                            <Input
                                label="당해 교육현황"
                                type="date"
                                value={formData.previous_training_date || ''}
                                onChange={(e) => setFormData({ ...formData, previous_training_date: e.target.value })}
                            />

                            <Input
                                label="교육 예정"
                                type="text"
                                value={formData.scheduled_education || ''}
                                onChange={(e) => setFormData({ ...formData, scheduled_education: e.target.value })}
                                placeholder="예: 2025년 상반기"
                            />
                        </>
                    )}

                    {/* PQ 교육 필드 */}
                    {(formData.education_type === 'ELECTRIC_PQ' || formData.education_type === 'FIRE_PQ') && (
                        <>
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                <Input
                                    label="시작일"
                                    type="date"
                                    value={formData.start_date}
                                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                                />

                                <Input
                                    label="종료일"
                                    type="date"
                                    value={formData.end_date}
                                    onChange={(e) => handleEndDateChange(e.target.value)}
                                />
                            </div>

                            <div>
                                <Input
                                    label="만료일"
                                    type="date"
                                    value={formData.expiry_date}
                                    onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                                />
                                <p className="mt-1 text-xs text-muted-foreground">PQ 교육은 종료일 입력 시 자동으로 계산됩니다 (종료일 + 3년)</p>
                            </div>

                            <Input
                                label="당해 교육현황"
                                type="date"
                                value={formData.previous_training_date || ''}
                                onChange={(e) => setFormData({ ...formData, previous_training_date: e.target.value })}
                            />

                            <Input
                                label="교육 예정"
                                type="text"
                                value={formData.scheduled_education || ''}
                                onChange={(e) => setFormData({ ...formData, scheduled_education: e.target.value })}
                                placeholder="예: 2025년 상반기"
                            />
                        </>
                    )}

                    <Input
                        label="비고"
                        type="text"
                        value={formData.remarks || ''}
                        onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                        placeholder="비고 사항 입력"
                    />
                </div>
            </Modal>

            {/* 상태 기준 안내 모달 */}
            <Modal
                isOpen={showStatusInfo}
                onClose={() => setShowStatusInfo(false)}
                title="교육 만료 상태 기준"
            >
                <ul className="divide-y divide-border">
                    {STATUS_LEGEND.map((item) => (
                        <li key={item.label} className="flex items-start gap-3 py-3 first:pt-0">
                            <Badge
                                variant={item.variant}
                                className={`w-20 shrink-0 justify-center ${item.className || ''}`}
                            >
                                {item.label}
                            </Badge>
                            <div className="flex-1 text-sm">
                                <div className="font-medium text-foreground">{item.title}</div>
                                <div className="mt-0.5 text-muted-foreground">{item.description}</div>
                            </div>
                        </li>
                    ))}
                </ul>

                <div className="mt-2 space-y-1 border-t border-border pt-3 text-xs text-muted-foreground">
                    <p>※ PQ 교육의 만료일은 종료일로부터 3년 후로 자동 계산됩니다.</p>
                    <p>※ 소방안전원 교육의 만료일은 수료년도로부터 2년 후 연말(12/31)로 자동 계산됩니다.</p>
                </div>
            </Modal>
        </>
    );
};

export default EducationManagement;
